import {Record} from 'immutable';
import moment from 'moment-timezone';
import {type Moment} from 'moment-timezone';

import type {PricedSessionBooking} from 'web-app/util/graphql/api-types';
import generateUUID from 'web-app/util/uuid';

export interface MonthlyPrice {
    price: number;
    pricingGroupId: string;
}

export type BookingType = 'ProductBooking' | 'SessionBooking';

interface ISessionBooking {
    readonly bookingType: BookingType;
    readonly day: string;
    readonly sessionId: string;
    readonly sessionVersion: string;
    readonly loading: boolean;
    readonly fundedMinutes: number | null;
    readonly monthlyPrices: MonthlyPrice[];
    readonly fundable: boolean | null;
    readonly bookingId: string;
    readonly start: Moment | null;
    readonly end: Moment | null;
    readonly planRuleId: string | null;
    readonly bookedPrice: number | null;
}

export class SessionBooking extends Record<ISessionBooking>({
    bookingType: 'SessionBooking',
    day: '',
    sessionId: '',
    sessionVersion: '',
    loading: false,
    fundedMinutes: null,
    monthlyPrices: [],
    fundable: null,
    bookingId: '',
    start: null,
    end: null,
    planRuleId: null,
    bookedPrice: 0,
}) {}

const fromAPI = (data: any) => {
    return new SessionBooking({
        ...data,
        start: data.start ? moment(data.start, 'HH:mm') : null,
        end: data.end ? moment(data.end, 'HH:mm') : null,
        bookingId: data.bookingId || generateUUID(),
    });
};

const fromGraphQL = (data: PricedSessionBooking) => {
    // If the session doesn't have flexible hours, start and end should not be populated.
    const flexibleHours = data.sessionAvailability.session.flexibleHours;
    const timeRange = flexibleHours ? data.range ?? data.sessionAvailability.timeRange : null;

    return new SessionBooking({
        day: data.sessionAvailability.day,
        sessionId: data.sessionAvailability.session.sessionId,
        sessionVersion: data.sessionAvailability.session.sessionVersionId,
        monthlyPrices: data.prices.map(price => ({
            price: Number.parseFloat(price.price),
            pricingGroupId: price.pricingGroupId,
        })),
        fundable: data.sessionAvailability.session.fundable,
        bookingId: data.sessionBookingId,
        start: timeRange?.from ? moment(timeRange.from, 'HH:mm:ss') : null,
        end: timeRange?.to ? moment(timeRange.to, 'HH:mm:ss') : null,
        planRuleId: data.planRuleId,
    });
};

export default {
    record: SessionBooking,
    default: new SessionBooking(),
    fromAPI,
    fromGraphQL,
};
